import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CompatibilityBreakdown } from "@/components/CompatibilityBreakdown";
import { toast } from "sonner";
import { ArrowLeftRight, MessageCircleQuestion, MessageCircle, Mail, Copy } from "lucide-react";

const loc = (p) => [p?.showCity !== false && p?.city, p?.showCountry !== false && p?.country].filter(Boolean).join(", ");
const list = (a) => (a || []).join(", ");
const h = (p) => (p?.height_cm ? `${p.height_cm} cm` : null);
const norm = (v) => String(v ?? "").trim().toLowerCase();
const differs = (a, b, same) => !same && !!norm(a) && !!norm(b) && norm(a) !== norm(b);

const buildRows = (me, them) => [
  ["Age", me?.age, them?.age],
  ["Location", loc(me), loc(them), norm(me?.country) === norm(them?.country)],
  ["Nationality", me?.nationality, them?.nationality],
  ["Ethnicity", me?.ethnicity, them?.showEthnicity !== false ? them?.ethnicity : null],
  ["Sect", me?.sect, them?.sect],
  ["Prayer", me?.prayer_habit, them?.prayer_habit],
  ["Religiosity", me?.religiosity, them?.religiosity],
  ["Marital", me?.marital_status, them?.marital_status],
  ["Children", me?.children, them?.children],
  ["Wants kids", me?.wantChildren, them?.wantChildren],
  ["Education", me?.education, them?.education],
  ["Profession", me?.profession, them?.profession],
  ["Height", h(me), h(them)],
  ["Languages", list(me?.languages), them?.showLanguages !== false ? list(them?.languages) : null],
  ["Relocation", me?.relocateChoice, them?.relocateChoice],
  ["Wali", me?.waliInvolvement, them?.waliInvolvement],
  ["Personality", me?.personality, them?.personality],
  ["Interests", list(me?.interests), list(them?.interests)],
  ["Hobbies", list(me?.hobbies), list(them?.hobbies)],
];

function ComparePhoto({ p, label, testid }) {
  const initials = (p?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");
  return (
    <div className="flex flex-col items-center gap-2" data-testid={testid}>
      <div className="aspect-[3/4] w-full max-w-[180px] overflow-hidden rounded-2xl border-2 border-emerald-100 bg-emerald-800 shadow-sm">
        {p?.photo ? <img src={p.photo} alt={label} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-serif text-4xl text-amber-200">{initials}</div>}
      </div>
      <p className="truncate font-serif text-lg font-semibold text-emerald-900">{label}{p?.age ? `, ${p.age}` : ""}</p>
    </div>
  );
}

function ShareBar({ me, them, rows }) {
  const name = them?.firstName || them?.name || "this member";
  const text = [
    `Nikah Match — comparison: ${me?.firstName || me?.name || "Me"} vs ${name}`,
    "",
    ...rows.map(([l, a, b, s]) => `${l}: ${a || "—"} | ${b || "—"}${differs(a, b, s) ? "  (differs)" : ""}`),
    "",
    "Shared for family / wali review.",
  ].join("\n");
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Comparison copied"); } catch { toast.error("Couldn't copy"); }
  };
  const btn = "flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors";
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-3" data-testid="compare-share">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Share with your wali or family</p>
      <div className="flex flex-wrap gap-2">
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="compare-share-whatsapp" className={`${btn} border-emerald-600/30 bg-emerald-50 text-emerald-800 hover:bg-emerald-100`}><MessageCircle className="h-4 w-4" /> WhatsApp</a>
        <a href={`mailto:?subject=${encodeURIComponent(`Profile comparison: ${name}`)}&body=${encodeURIComponent(text)}`} data-testid="compare-share-email" className={`${btn} border-emerald-900/15 text-emerald-900 hover:bg-mint`}><Mail className="h-4 w-4" /> Email</a>
        <button onClick={copy} data-testid="compare-share-copy" className={`${btn} border-emerald-900/15 text-emerald-900 hover:bg-mint`}><Copy className="h-4 w-4" /> Copy</button>
      </div>
    </div>
  );
}

export function CompareDialog({ open, onOpenChange, me, them, onAsk }) {
  const rows = buildRows(me, them);
  const diffCount = rows.filter(([, a, b, s]) => differs(a, b, s)).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-2xl text-emerald-900"><ArrowLeftRight className="h-5 w-5 text-amber-600" /> Compare Profiles</DialogTitle></DialogHeader>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <ComparePhoto p={me} label="You" testid="compare-photo-me" />
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-600">vs</span>
          <ComparePhoto p={them} label={them?.firstName || them?.name} testid="compare-photo-them" />
        </div>
        <p className="flex items-center justify-center gap-2 text-xs text-slate-500" data-testid="compare-diff-count">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" /> {diffCount} difference{diffCount === 1 ? "" : "s"} highlighted
        </p>
        <div className="space-y-1 border-y border-emerald-900/10 py-2" data-testid="compare-rows">
          {rows.map(([label, a, b, s]) => {
            const d = differs(a, b, s);
            return (
              <div key={label} data-testid={`compare-row-${label.toLowerCase().replace(/\s+/g, "-")}`} data-differs={d}
                className={`grid grid-cols-[1fr_88px_1fr] items-center gap-x-3 rounded-lg px-2 py-2 text-sm ${d ? "border-l-4 border-amber-500 bg-amber-50" : "odd:bg-mint/40"}`}>
                <span className={`text-right ${d ? "font-semibold text-amber-900" : "text-slate-700"}`}>{a || "—"}</span>
                <span className={`text-center text-[10px] font-semibold uppercase tracking-wider ${d ? "text-amber-600" : "text-emerald-700/70"}`}>{label}</span>
                <span className={d ? "font-semibold text-amber-900" : "text-slate-700"}>{b || "—"}</span>
              </div>
            );
          })}
        </div>
        <ShareBar me={me} them={them} rows={rows} />
        <CompatibilityBreakdown me={me} them={them} className="border-0 p-0 shadow-none" />
        {onAsk && <button onClick={() => { onOpenChange(false); onAsk(); }} data-testid="compare-ask-question" className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-800/30 bg-mint px-4 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"><MessageCircleQuestion className="h-4 w-4" /> Ask a Question</button>}
      </DialogContent>
    </Dialog>
  );
}
